import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import {
  ModalContext,
  ModalModel,
  useBreakpointValue,
} from '@notino/react-styleguide';
import {
  GetProductImageQuery,
  GetProductImageQueryVariables,
} from '@notino/shared/definitions/types';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { trackDrawerClose } from '@components/BottomSheet/tracking';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { useProductDetailContext } from '../../ProductDetailContext';

import { AvailableSetsList } from './AvailableSetsList';
import getProductImageQuery from './getProductImage.graphql';
import { messages } from './messages';
import {
  ImagePlaceholder,
  StyledCdnImage,
  Wrapper,
  Chevron,
  DiscoverSetsText,
} from './styled';
import { trackElementClick } from './tracking';
import { getDiscoverSetsMessage } from './utils';

const AvailableSets = () => {
  const { inView, ref } = useInView({ triggerOnce: true });
  const isDesktop = useBreakpointValue({ md: true });
  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const { toggleDrawer } = useDrawer();
  const { getTimeFromInit } = useTrackingContext();
  const { formatMessage } = useIntl();
  const {
    product: {
      features: { availableSets },
    },
  } = useProductDetailContext();

  const sets = availableSets?.sets ?? [];

  const { data, loading } = useQuery<
    GetProductImageQuery,
    GetProductImageQueryVariables
  >(getProductImageQuery, {
    skip: sets.length === 0,
    variables: { webId: String(sets[0]?.webId) },
    ssr: false,
  });

  if (sets.length === 0) {
    return null;
  }

  const openAvailableSets = () => {
    trackElementClick({
      timing: getTimeFromInit(),
      action: 'gif_sets_open',
      type: 'gift_sets',
      name: 'gift_sets_open',
      interaction: 'click',
    });

    if (isDesktop) {
      toggleModal(<AvailableSetsList sets={sets} />, {
        size: ModalModel.Sizes.large,
        onClose: () => {
          trackElementClick({
            timing: getTimeFromInit(),
            name: 'gift_sets_close',
            type: 'gift_sets',
            action: 'gift_sets_close',
          });
          hideModal();
        },
      });
      return;
    }
    toggleDrawer(<AvailableSetsList sets={sets} />, {
      onDismiss: (closeType) =>
        trackDrawerClose({
          closeType,
          timing: getTimeFromInit(),
          name: 'gift_sets_close',
          type: 'gift_sets',
          action: 'gift_sets_close',
        }),
      header: formatMessage(messages.setsWithThisProduct),
      ...(sets.length >= 3 && { ignoreBodySwipe: true }),
    });
  };

  const imageUrl = inView && !loading && data?.vpProductById?.imageUrl;

  return (
    <Wrapper
      ref={ref}
      onClick={openAvailableSets}
      data-testid="available-sets-wrapper"
    >
      {imageUrl ? (
        <StyledCdnImage src={imageUrl} sourceKey="order" />
      ) : (
        <ImagePlaceholder />
      )}

      <DiscoverSetsText>
        <FormattedMessage {...getDiscoverSetsMessage(availableSets.sets)} /> (
        {sets.length})
      </DiscoverSetsText>

      <Chevron />
    </Wrapper>
  );
};

export default AvailableSets;
